import React from 'react'
import { Box, Group, ActionIcon, Select } from '@mantine/core'
import { DateTime } from 'luxon'
import { WidgetContextProvider } from '../providers'
import { YearPickerInput, MonthPickerInput } from '@mantine/dates'
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react'
import { QuarterPickerInput } from './QuarterPickerInput'
import { SortableWidgetChildren } from '../SortableComponents'
import { ContainerWrapper } from '../ContainerWrapper'
import { useWidgetEditor } from '../state'
import { ContainerForm } from '../ContainerForm'
import cls from './common.module.css'
import { PeriodContainerOptions } from '../schemas'

export default function PeriodContainer({ id }: { id: string }) {
  const [periodType, setPeriodType] = React.useState<
    'year' | 'quarter' | 'month'
  >('year')
  const [dateValue, setDateValue] = React.useState<string | null>('2025-01-01')

  const editor = useWidgetEditor()
  const config = editor.useConfig(id)

  if (config.widget !== 'PeriodContainer')
    throw new Error('Invalid widget type, expected PeriodContainer')

  const currentDate = dateValue ? DateTime.fromISO(dateValue) : DateTime.now()
  const period = currentDate.startOf(periodType).toISODate()

  const navUnit =
    periodType === 'year'
      ? { years: 1 }
      : periodType === 'quarter'
        ? { quarters: 1 }
        : { months: 1 }

  const handlePrevious = () => {
    setDateValue(currentDate.minus(navUnit).toISODate())
  }

  const handleNext = () => {
    setDateValue(currentDate.plus(navUnit).toISODate())
  }

  return (
    <ContainerWrapper
      name="Period Picker"
      id={id}
      onCancel={editor.endEditWidget}
      onSave={editor.save}
    >
      <Box
        style={{
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          padding: '16px',
          backgroundColor: '#eee',
        }}
      >
        <Group gap="xs" mb="md" align="flex-end">
          <Select
            label="Period Type"
            value={periodType}
            onChange={(value) =>
              setPeriodType(value as 'year' | 'quarter' | 'month')
            }
            data={[
              { value: 'year', label: 'Year' },
              { value: 'quarter', label: 'Quarter' },
              { value: 'month', label: 'Month' },
            ]}
            style={{ maxWidth: '120px' }}
          />

          {periodType === 'year' && (
            <YearPickerInput
              label="Select Year"
              value={dateValue}
              onChange={(value) => setDateValue(value)}
              valueFormat="YYYY"
              style={{ maxWidth: '200px', minWidth: '100px' }}
              clearable
              placeholder="Choose a year"
            />
          )}
          {periodType === 'quarter' && (
            <div style={{ maxWidth: '260px', minWidth: '200px' }}>
              <QuarterPickerInput
                value={dateValue}
                onChange={(value) => setDateValue(value || null)}
              />
            </div>
          )}
          {periodType === 'month' && (
            <MonthPickerInput
              label="Select Month"
              value={dateValue}
              onChange={(value) => setDateValue(value)}
              valueFormat="MMM YYYY"
              style={{ maxWidth: '200px', minWidth: '100px' }}
              clearable
              placeholder="Choose a month"
            />
          )}

          <ActionIcon variant="filled" onClick={handlePrevious} size="lg">
            <IconChevronLeft size={20} />
          </ActionIcon>
          <ActionIcon variant="filled" onClick={handleNext} size="lg">
            <IconChevronRight size={20} />
          </ActionIcon>
        </Group>

        <WidgetContextProvider
          context={{ period_type: periodType, period: period || '2025-01-01' }}
        >
          <Group gap={8} className={cls.flexFill}>
            <SortableWidgetChildren id={id} dir="horizontal" />
          </Group>
        </WidgetContextProvider>
      </Box>
    </ContainerWrapper>
  )
}

export const PeriodContainerRegistry = {
  component: PeriodContainer,
  formComponent: ContainerForm,
  mock: () => null,
  getInitialOptions: () => PeriodContainerOptions.parse({}),
}
